import { DemandManagementWrapper, Notification } from 'components/DemandManagement/style';
import { Divider, InlineLink, TextFlow } from 'components/Typography';
import { STRINGS } from 'config';
import { useAppDispatch, useAppState } from 'store/configureStore';
import React, { useEffect, useMemo } from 'react';
import { apiGetAdminAvailability, getDemandManagementEnabled } from 'store/action/demandManagement';
import { TableHeaderDivider, TableHeaderSmallText, TableHeaderTitleText } from 'components/Table';
import { HeaderColumn, HeaderRow } from 'components/Table/tableHeader';
import { ScheduledClosures } from 'components/ScheduledClosures';
import { AdminAvailability } from 'components/AdminAvailability';
import { CustomClosureMessage } from 'components/CustomClosureMessage';
import { Feature, RenderOff, RenderOn } from '@econsult/econsult-ui/dist/components/FlagControlledFeature';
import { ClosingTimeBehaviour } from 'components/ClosingTimeBehaviour';
import { MoreInfo } from './moreInfo';
import { ConsultationResponseTime } from './ConsultationResponseTime';

const {
  PRIMARY_CARE_DOMAIN,
  REQUEST_STATUS: { PENDING, IDLE },
  FEATURE_FLAGS: {
    SETTINGS_FRONTEND_CUSTOM_CLOSURE_MESSAGE_ENABLED,
    CONSULTATION_RESPONSE_TIME_ENABLED,
    SETTINGS_CLOSING_TIME_BEHAVIOUR_ENABLED,
  },
} = STRINGS;

const DMDisabledNoticeText = () => (
  <>
    Your practice doesn&apos;t have demand management enabled. If you need to change this, please contact us through Live Chat or email at&nbsp;
    <InlineLink href="mailto:practice.support@econsult.health">practice.support@econsult.health</InlineLink>.
  </>
);

export const DemandManagement = () => {
  const dispatch = useAppDispatch();
  const {
    app: {
      selectedPractice,
    },
    demandManagement: {
      demandManagementEnabled,
      demandManagementEnabledStatus,
    },
  } = useAppState();

  const demandManagementSettingUrl = useMemo(() => (
    `https://${selectedPractice.id}.${PRIMARY_CARE_DOMAIN}/manage/${selectedPractice.pcmCode}`
  ), [selectedPractice.id, selectedPractice.pcmCode]);

  const showDMDisabledNotice = !demandManagementEnabled && ![PENDING, IDLE].includes(demandManagementEnabledStatus);

  useEffect(() => {
    apiGetAdminAvailability(selectedPractice.id)(dispatch);
    getDemandManagementEnabled(selectedPractice.id)(dispatch);
  }, [dispatch, selectedPractice.id]);

  return (
    <DemandManagementWrapper id="SkipToUserListMainContent" data-automation="DemandManagementWrapper">
      <HeaderColumn>
        <HeaderRow>
          <HeaderColumn>
            <TableHeaderTitleText as="h1" data-automation="DemandManagementPageTitleText">
              Demand management
            </TableHeaderTitleText>
            <TableHeaderSmallText data-automation="DemandManagementPageSubTitleText">
              Manage your eConsult demand to suit your practice&apos;s needs.
            </TableHeaderSmallText>
          </HeaderColumn>
        </HeaderRow>
        <TableHeaderDivider />
      </HeaderColumn>

      <TextFlow>
        {showDMDisabledNotice && (
          <Notification automationName="demandManagementDisabledNotice" textContent={<DMDisabledNoticeText />} />
        )}

        <ScheduledClosures />

        <Feature flags={[CONSULTATION_RESPONSE_TIME_ENABLED]}>
          <RenderOn>
            <ConsultationResponseTime />
          </RenderOn>
        </Feature>

        <Feature flags={[SETTINGS_FRONTEND_CUSTOM_CLOSURE_MESSAGE_ENABLED]}>
          <RenderOn>
            <CustomClosureMessage />
          </RenderOn>
        </Feature>

        <AdminAvailability />

        <Feature flags={[SETTINGS_CLOSING_TIME_BEHAVIOUR_ENABLED]}>
          <RenderOn>
            <ClosingTimeBehaviour />
          </RenderOn>

          <RenderOff>
            <Divider />
          </RenderOff>
        </Feature>

        <MoreInfo
          demandManagementEnabled={demandManagementEnabled}
          demandManagementSettingUrl={demandManagementSettingUrl}
        />
      </TextFlow>
    </DemandManagementWrapper>
  );
};
